import React from "react";
import { motion } from "framer-motion";
import { FaCode, FaServer, FaTools, FaDatabase } from "react-icons/fa";
import { SiPowerapps, SiDotnet, SiPlaywright, SiGithubactions, SiTypescript } from "react-icons/si";
import { FaJava } from "react-icons/fa6";
import { FiGithub, FiExternalLink } from "react-icons/fi";

const Experience = () => {
  const technologies = [
    // Languages & Frontend
    { name: "JavaScript", category: "fullstack", icon: <FaCode className="text-yellow-500 dark:text-yellow-400" /> },
    { name: "TypeScript", category: "language", icon: <SiTypescript className="text-blue-600 dark:text-blue-500" /> },
    { name: "React", category: "frontend", icon: <FaCode className="text-blue-500 dark:text-blue-400" /> },
    { name: "Tailwind CSS", category: "frontend", icon: <FaCode className="text-cyan-500 dark:text-cyan-400" /> },

    // Backend
    { name: "PHP", category: "backend", icon: <FaServer className="text-purple-500" /> },
    { name: "Laravel", category: "backend", icon: <FaServer className="text-red-500" /> },
    { name: ".NET", category: "backend", icon: <SiDotnet className="text-purple-600" /> },
    { name: "Java", category: "backend", icon: <FaJava className="text-red-600" /> },
    { name: "Express.js", category: "backend", icon: <FaServer className="text-gray-500 dark:text-gray-400" /> },

    // Database
    { name: "MySQL", category: "database", icon: <FaDatabase className="text-blue-400" /> },
    { name: "MongoDB", category: "database", icon: <FaDatabase className="text-green-500" /> },

    // Tools & Testing
    { name: "Git/GitHub", category: "tools", icon: <FaTools className="text-gray-500 dark:text-gray-300" /> },
    { name: "PowerApps", category: "tools", icon: <SiPowerapps className="text-blue-500" /> },
    { name: "Playwright", category: "testing", icon: <SiPlaywright className="text-green-600" /> },
    { name: "GitHub Actions", category: "ci/cd", icon: <SiGithubactions className="text-sky-500" /> },
    { name: "Selenium", category: "testing", icon: <FaTools className="text-green-600" /> },
    { name: "Cucumber", category: "testing", icon: <FaTools className="text-emerald-500" /> },
  ];

  const experiences = [
    {
      role: "QA Automation Engineer (PFA) — SuiteCRM E2E",
      company: "pfa-suitecrm-tests",
      period: "2024 – 2025",
      achievements: [
        "Built end‑to‑end automated tests for SuiteCRM with Playwright (TypeScript).",
        "Implemented GitHub Actions pipeline to install deps, run tests, generate HTML report, and upload artifacts.",
        "Enabled local workflow: npx playwright test and npx playwright show-report for rapid feedback.",
        "Configured screenshots/trace on failure and rich HTML reporting.",
        "Adopted CI/CD best practices: fast feedback, repeatability, and artifact archiving."
      ],
      github: "https://github.com/mohammedyassinAa/pfa-suitecrm-tests"
    },
    {
      role: "Full‑Stack Developer — Cabinet Dentaire Bakraoui",
      company: "Freelance (Client)",
      period: "2024 – Present",
      achievements: [
        "Delivered a production web platform for a dental clinic with online booking, services, team pages, and contact with Google Maps.",
        "Built a secure admin dashboard (login/JWT) to manage appointments, filter by date range, delete entries, and export lists to PDF (jsPDF + autotable).",
        "Implemented responsive UI with dark/light mode, smooth animations (Framer Motion), and accessibility basics.",
        "Structured a Node.js + Express + MySQL backend with environment‑based config and CORS; handled hashing and auth securely.",
      ],
      live: "https://www.centre-dentaire-bakraoui.com/",
      repos: [
        { label: "Frontend", url: "https://github.com/mohammedyassinAa/Bakraoui" },
        { label: "Backend", url: "https://github.com/mohammedyassinAa/bakraoui-backend" }
      ]
    },
    {
      role: "PowerApps Developer Intern",
      company: "Abcdis",
      period: "2023 (3 months)",
      achievements: [
        "Developed sales management application using PowerApps and SharePoint.",
        "Digitalized sales process improving field agent efficiency by 40%.",
        "Implemented client data management and order tracking systems.",
        "Designed mobile-friendly UI for field agents with offline capabilities."
      ]
    },
    {
      role: "Test Automation",
      company: "NTT Data",
      period: "April 2023 - May 2023",
      achievements: [
        "Collaborated in test automation using Selenium, Cucumber, and Maven.",
        "Implemented efficient testing scripts for quality assurance.",
        "Contributed to early identification of software issues.",
        "Worked with Agile methodologies in cross-functional teams."
      ]
    },
    {
      role: "Full‑Stack Developer",
      company: "Freelance",
      period: "2021 - Present",
      achievements: [
        "Developed WEGYM fitness management system using PHP and MySQL.",
        "Building EduMatch platform with Laravel and ReactJS.",
        "Created responsive user interfaces with modern frameworks.",
        "Implemented RESTful APIs and database solutions."
      ]
    }
  ];

  return (
    <div
      name="experience"
      id="experience"
      className="w-full min-h-screen pt-24 md:pt-32 transition-colors duration-300
                 bg-gradient-to-b from-gray-50 via-white to-gray-100
                 dark:from-gray-900 dark:via-black dark:to-gray-800"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 md:py-20 flex flex-col justify-center">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mb-12 md:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 inline-block text-gray-900 dark:text-white">
            Experience & Skills
            <span className="block mt-2 h-1 w-32 bg-gradient-to-r from-cyan-500 to-blue-600 rounded"></span>
          </h2>
          <p className="text-gray-700 dark:text-gray-300 text-base sm:text-lg max-w-3xl">
            My professional journey and technical expertise
          </p>
        </motion.div>

        {/* Work Experience */}
        <div className="mb-16 md:mb-20">
          <h3 className="text-2xl font-bold mb-8 text-cyan-600 dark:text-cyan-400 flex items-center gap-2">
            <FaCode className="text-cyan-600 dark:text-cyan-400" /> Professional Experience
          </h3>

          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <motion.div
                key={`${exp.role}-${index}`}
                initial={{ x: index % 2 === 0 ? -40 : 40, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                className="rounded-xl p-6 md:p-7 border shadow-sm
                           bg-white/70 border-gray-200
                           dark:bg-gray-800/50 dark:border-gray-700
                           hover:border-cyan-400/60 hover:shadow-cyan-500/10 transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 mb-4">
                  <div>
                    <h4 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">{exp.role}</h4>
                    <p className="text-cyan-700 dark:text-cyan-400">{exp.company}</p>
                  </div>
                  <div className="flex flex-col md:items-end gap-2">
                    <span className="text-cyan-700 dark:text-cyan-400 text-sm md:text-base">
                      {exp.period}
                    </span>
                    <div className="flex flex-wrap gap-3">
                      {exp.live && (
                        <a
                          href={exp.live}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium
                                     text-cyan-700 hover:text-cyan-800
                                     dark:text-cyan-400 dark:hover:text-cyan-300 transition-colors"
                          aria-label="Open live site"
                        >
                          <FiExternalLink className="text-lg" />
                          Live
                        </a>
                      )}
                      {exp.repos?.map((r) => (
                        <a
                          key={r.url}
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium
                                     text-gray-700 hover:text-cyan-600
                                     dark:text-gray-300 dark:hover:text-cyan-400 transition-colors"
                          aria-label={`Open ${r.label} repository`}
                        >
                          <FiGithub className="text-lg" />
                          {r.label}
                        </a>
                      ))}
                      {exp.github && !exp.repos && (
                        <a
                          href={exp.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium
                                     text-gray-700 hover:text-cyan-600
                                     dark:text-gray-300 dark:hover:text-cyan-400 transition-colors"
                          aria-label="Open repository"
                        >
                          <FiGithub className="text-lg" />
                          Repo
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                  {exp.achievements.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Skills */}
        <div>
          <h3 className="text-2xl font-bold mb-8 text-cyan-600 dark:text-cyan-400 flex items-center gap-2">
            <FaTools className="text-cyan-600 dark:text-cyan-400" /> Technical Skills
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {technologies.map((tech, index) => (
              <motion.div
                key={`${tech.name}-${index}`}
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.3, delay: index * 0.04 }}
                className="p-4 rounded-lg border text-center
                           bg-white/70 border-gray-200
                           dark:bg-gray-800/50 dark:border-gray-700
                           hover:border-cyan-400/60 transition-all duration-300 flex flex-col items-center gap-2"
              >
                <div className="text-2xl">{tech.icon}</div>
                <span className="font-medium text-gray-900 dark:text-white">{tech.name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{tech.category}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Experience;